import React, { useState, useEffect } from 'react';
import ChildrenPage from './pages/ChildrenPage';
import SortPage from './pages/SortPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import AdminUsagePage from './pages/AdminUsagePage';
import UsagePage from './pages/UsagePage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import TwoFactorVerifyPage from './pages/TwoFactorVerifyPage';
import TwoFactorSetupPage from './pages/TwoFactorSetupPage';
import ProfilePage from './pages/ProfilePage';
import OnboardingPage from './pages/OnboardingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CommerceLawPage from './pages/CommerceLawPage';
import LandingPage from './pages/LandingPage';
import { me, logout } from './api/auth';

// 法的ページ（プライバシー・利用規約・特商法）は path ルーティングで独立 URL に。
// Google OAuth verification が「ホームページに PP リンクが掲載されていない」と判定
// するのを防ぐ（hash だと別ページとして認識されない）。
// アプリ内ナビゲーションは引き続き hash ベース。
const STATIC_PATH_PAGES = new Set(['privacy', 'terms', 'commerce-law']);

function parseRoute() {
  // パスベース: /privacy, /terms, /commerce-law
  const path = window.location.pathname.replace(/^\//, '').replace(/\/$/, '');
  if (STATIC_PATH_PAGES.has(path)) {
    return { page: path, params: new URLSearchParams() };
  }
  // ハッシュベース（アプリ内ルーティング + 旧 URL 後方互換）
  const hash = window.location.hash.replace(/^#/, '');
  const [page, qs] = hash.split('?');
  const params = new URLSearchParams(qs);
  return { page, params };
}

const NAV_USER = [
  { id: 'children',   label: '顔登録',      icon: '👶' },
  { id: 'sort',       label: '写真振り分け', icon: '🌈' },
  { id: 'usage',      label: '利用実績',    icon: '📊' },
  { id: 'onboarding', label: 'ガイド',      icon: '📖' },
  { id: 'profile',    label: 'プロフィール', icon: '👤' },
];
const NAV_ADMIN = [
  { id: 'children',     label: '顔登録',       icon: '👶' },
  { id: 'sort',         label: '写真振り分け',  icon: '🌈' },
  { id: 'admin',        label: 'ユーザー管理', icon: '🛠️' },
  { id: 'admin-usage',  label: '利用実績',     icon: '📊' },
  { id: 'onboarding',   label: 'ガイド',       icon: '📖' },
  { id: 'profile',      label: 'プロフィール', icon: '👤' },
];

const BUBBLE_STYLES = [
  { width: 90,  height: 90,  top: '-18px',  right: '-20px', opacity: 0.13 },
  { width: 55,  height: 55,  top: '120px',  left: '-18px',  opacity: 0.10 },
  { width: 38,  height: 38,  top: '210px',  right: '18px',  opacity: 0.09 },
  { width: 70,  height: 70,  bottom: '90px',right: '-22px', opacity: 0.11 },
  { width: 44,  height: 44,  bottom: '30px',left: '20px',   opacity: 0.09 },
];

export default function App() {
  const [user, setUser]             = useState(null);   // null = 未確認, false = 未ログイン
  const [active, setActive]         = useState('children');
  const [hashPage, setHashPage]     = useState(() => parseRoute());
  const [showForgot, setShowForgot] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [twoFaTempToken, setTwoFaTempToken] = useState(null);
  const [show2faSetup, setShow2faSetup]     = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const checkAuth = async () => {
    try {
      const data = await me();
      setUser(data);
    } catch {
      setUser(false);
    }
  };

  useEffect(() => {
    checkAuth();
    const handleLogout = () => { setUser(false); };
    const handleHash   = () => { setHashPage(parseRoute()); };
    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('hashchange', handleHash);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('hashchange', handleHash);
    };
  }, []);

  const clearHash = () => { window.location.hash = '#app'; };

  const handleLogout = async () => {
    try { await logout(); } catch { /* サーバー側のエラーは無視してフロントだけ落とす */ }
    setUser(false);
    setActive('children');
    window.location.hash = '';
  };

  // ランディングページ（ハッシュなし or #home）
  if (!hashPage.page || hashPage.page === 'home') {
    // ログイン済みなら自動でアプリへ
    if (user) { window.location.hash = '#app'; return null; }
    return <LandingPage onStart={() => { window.location.hash = '#app'; }} />;
  }

  // 利用規約（ログイン不要）
  if (hashPage.page === 'terms') {
    return (
      <div style={{ height: '100vh', background: 'var(--bg)', overflowY: 'auto' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10 }}>
          <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>← 戻る</button>
        </div>
        <TermsPage standalone={true} />
      </div>
    );
  }

  // プライバシーポリシー（ログイン不要）
  if (hashPage.page === 'privacy') {
    return (
      <div style={{ height: '100vh', background: 'var(--bg)', overflowY: 'auto' }}>
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10 }}>
          <button onClick={() => window.history.back()} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>← 戻る</button>
        </div>
        <PrivacyPage standalone={true} />
      </div>
    );
  }

  // 特定商取引法に基づく表記（ログイン不要）
  if (hashPage.page === 'commerce-law') {
    return <CommerceLawPage onBack={() => window.history.back()} />;
  }

  // 招待リンク
  if (hashPage.page === 'invite') {
    const token = hashPage.params.get('token');
    return <AcceptInvitePage token={token} onComplete={() => { clearHash(); checkAuth(); }} />;
  }

  // パスワードリセットリンク
  if (hashPage.page === 'reset') {
    const token = hashPage.params.get('token');
    return (
      <ResetPasswordPage
        token={token}
        onComplete={() => { clearHash(); checkAuth(); }}
        on2faRequired={(tempToken) => { clearHash(); setTwoFaTempToken(tempToken); }}
      />
    );
  }

  // メールアドレス確認リンク
  if (hashPage.page === 'verify-email') {
    const token = hashPage.params.get('token');
    return <VerifyEmailPage token={token} onComplete={() => { clearHash(); setUser(false); }} />;
  }

  // 認証状態確認中
  if (user === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <span className="spinner spinner-dark" style={{ width: 32, height: 32 }} />
      </div>
    );
  }

  // パスワードを忘れた
  if (showForgot) {
    return <ForgotPasswordPage onBack={() => setShowForgot(false)} />;
  }

  // 新規登録
  if (showRegister) {
    return <RegisterPage onBack={() => setShowRegister(false)} />;
  }

  // 2FA 認証待ち
  if (twoFaTempToken) {
    return (
      <TwoFactorVerifyPage
        tempToken={twoFaTempToken}
        onSuccess={() => { setTwoFaTempToken(null); checkAuth(); }}
        onBack={() => setTwoFaTempToken(null)}
      />
    );
  }

  // 未ログイン
  if (user === false) {
    return (
      <LoginPage
        onLogin={(data) => {
          if (data?.requires_2fa) {
            setTwoFaTempToken(data.temp_token);
          } else {
            checkAuth();
          }
        }}
        onForgot={() => setShowForgot(true)}
        onRegister={() => setShowRegister(true)}
      />
    );
  }

  const nav = user.role === 'admin' ? NAV_ADMIN : NAV_USER;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="app-shell">

      {/* モバイル用ハンバーガーボタン（CSS で 768px 以下のみ表示） */}
      <button
        className="hamburger"
        onClick={() => setMobileMenuOpen(o => !o)}
        aria-label="メニュー"
      >
        {mobileMenuOpen ? '×' : '☰'}
      </button>

      {/* モバイル用バックドロップ（タップで閉じる） */}
      <div
        className={`sidebar-backdrop ${mobileMenuOpen ? 'open' : ''}`}
        onClick={closeMobileMenu}
      />

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${mobileMenuOpen ? 'sidebar-open' : ''}`}>

        {BUBBLE_STYLES.map((s, i) => (
          <div key={i} style={{
            position: 'absolute',
            width: s.width, height: s.height,
            borderRadius: '50%',
            background: '#fff',
            opacity: s.opacity,
            top: s.top, bottom: s.bottom,
            left: s.left, right: s.right,
            pointerEvents: 'none',
          }} />
        ))}

        {/* Brand */}
        <div style={{
          padding: '28px 20px 22px',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
          position: 'relative',
        }}>
          <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 8 }}>📸</div>
          <div style={{
            fontFamily: "'Nunito', 'M PLUS Rounded 1c', sans-serif",
            fontSize: 22, fontWeight: 900,
            color: '#fff',
            letterSpacing: '-0.01em',
            lineHeight: 1.1,
            textShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}>
            えがおフォルダ
          </div>
          <div style={{
            marginTop: 5, fontSize: 10, fontWeight: 700,
            color: 'rgba(255,255,255,0.65)',
            letterSpacing: '0.06em',
          }}>
            写真を自動でお片付け ✨
          </div>
        </div>

        {/* Nav */}
        <nav style={{ padding: '14px 10px', flex: 1, position: 'relative' }}>
          {nav.map(item => (
            <button
              key={item.id}
              onClick={() => { setActive(item.id); closeMobileMenu(); }}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 14px',
                marginBottom: 7,
                borderRadius: 16,
                border: 'none',
                cursor: 'pointer',
                background: active === item.id
                  ? 'rgba(255,255,255,0.25)'
                  : 'rgba(255,255,255,0.09)',
                color: '#fff',
                fontSize: 13,
                fontFamily: "'M PLUS Rounded 1c', 'Nunito', sans-serif",
                fontWeight: active === item.id ? 800 : 500,
                textAlign: 'left',
                transition: 'all 0.2s cubic-bezier(.34,1.56,.64,1)',
                boxShadow: active === item.id
                  ? '0 4px 14px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.3)'
                  : 'none',
                transform: active === item.id ? 'scale(1.02)' : 'scale(1)',
              }}
            >
              <span style={{ fontSize: 20, lineHeight: 1 }}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User info + 2FA + Logout */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,0.15)',
          position: 'relative',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', wordBreak: 'break-all' }}>
            {user.email}
          </div>
          <button
            onClick={() => setShow2faSetup(true)}
            style={{
              width: '100%', padding: '5px 0', borderRadius: 8, border: 'none',
              background: user.totp_enabled ? 'rgba(6,214,160,0.25)' : 'rgba(255,255,255,0.12)',
              color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {user.totp_enabled ? '🔐 2FA 有効' : '2FA を設定'}
          </button>
          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: '5px 0', borderRadius: 8, border: 'none',
              background: 'rgba(255,255,255,0.12)', color: '#fff',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
            }}
          >
            ログアウト
          </button>
        </div>

        {/* Footer */}
        <div style={{
          padding: '10px 20px',
          fontSize: 9,
          color: 'rgba(255,255,255,0.50)',
          letterSpacing: '0.05em',
          fontWeight: 600,
          position: 'relative',
        }}>
          Powered by AWS Rekognition<br />
          <span style={{ marginTop: 4, display: 'inline-block', lineHeight: 1.7 }}>
            <a href="/terms" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'underline', marginRight: 8 }}>利用規約</a>
            <a href="/privacy" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'underline', marginRight: 8 }}>プライバシー</a>
            <a href="/commerce-law" style={{ color: 'rgba(255,255,255,0.45)', textDecoration: 'underline' }}>特商法表記</a>
          </span>
        </div>
      </aside>

      {/* ── Content ── */}
      <main className="app-main">
        {active === 'children'    && <ChildrenPage />}
        {active === 'sort'        && <SortPage />}
        {active === 'usage'       && user.role !== 'admin' && <UsagePage />}
        {active === 'admin'       && user.role === 'admin' && <AdminPage />}
        {active === 'admin-usage' && user.role === 'admin' && <AdminUsagePage />}
        {active === 'onboarding'  && <OnboardingPage />}
        {active === 'profile'     && (
          <ProfilePage
            user={user}
            onUserUpdate={(updated) => setUser(u => ({ ...u, ...updated }))}
          />
        )}
      </main>

      {/* 2FA セットアップモーダル */}
      {show2faSetup && (
        <TwoFactorSetupPage onClose={() => { setShow2faSetup(false); checkAuth(); }} />
      )}
    </div>
  );
}
